import Notifications from '@components/Notifications/Notifications';
import TopNav from '@components/TopNav/TopNav';
import { upgradeFlowContext } from '@context/upgrade-flow-context';
import { useUser } from '@context/user-context';
import { useActorPlan } from '@hooks/actor/use-actor-plan';
import { useOpenAppUrl } from '@hooks/use-open-app-url';
import { useGlobalModal } from '@modules/global-modals/use-global-modal';
import { filter } from '@modules/react-children-utilities';
import Head from 'next/head';
import { type FC, isValidElement, useEffect } from 'react';

import styles from './FocusLayout.module.scss';

interface FocusLayoutProps {
  title: string;
}

const FocusLayout: FC<React.PropsWithChildren<FocusLayoutProps>> = ({ children, title }) => {
  const { loading: loadingUserDetails, ...userDetails } = useUser();
  const { user } = userDetails;
  const { planName } = useActorPlan();
  const { openModal } = useGlobalModal({
    registeredModals: ['UPGRADE'],
  });

  const topNav = filter(children, (child) => isValidElement(child) && child?.type === TopNav) ?? TopNav;
  const sideEditor = filter(
    children,
    (child) => isValidElement(child) && child?.type === FocusLayoutSideEditorContainer,
  );
  const content = filter(
    children,
    (child) =>
      isValidElement(child) && child?.type !== FocusLayoutSideEditorContainer && child?.type !== TopNav,
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: historic
  useEffect(() => {
    if (!user || !user.signInEmailAddress || process.env.NODE_ENV !== 'production') return;
  }, [user, planName]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: historic
  useEffect(() => {
    const showUpgradeDialog = localStorage.getItem('showUpgradePrompt') === 'true';

    if (showUpgradeDialog) {
      localStorage.removeItem('showUpgradePrompt');
      openModal({
        type: 'UPGRADE',
        trackingSource: 'focus-layout',
      });
    }
  }, []);

  const { appStore } = useOpenAppUrl({ includeOpaqueToken: false });

  return (
    <upgradeFlowContext.Provider value={{ flowType: 'in-app' }}>
      <div className={styles['layout']}>
        <Head>
          <title>{title}</title>
        </Head>

        <div className={styles['mobile-warning']}>
          <p className={styles['mobile-warning-text']}>
            The Blinq dashboard isn&apos;t currently optimised for mobile. For the best mobile experience
            download the Blinq app using the following link.
          </p>
          <a href={appStore} className={styles['blinq-app-link']}>
            Download the app
          </a>
        </div>
        <div className={styles['focus-mode-contents']}>
          {topNav}
          <div className={styles['focus-mode-body']} data-has-top-nav={topNav.length > 0}>
            {sideEditor}

            <div className={styles['focus-mode-edited-contents']}>
              {content}

              <div className={styles['focus-notifications-section']}>
                <Notifications />
              </div>
            </div>
          </div>
        </div>
      </div>
    </upgradeFlowContext.Provider>
  );
};

const FocusLayoutSideEditorContainer: FC<React.PropsWithChildren<unknown>> = ({ children }) => (
  <div className={styles['focus-mode-editing-bar']}>{children}</div>
);

export default FocusLayout;
export { FocusLayoutSideEditorContainer };
